"use client";

import TFLink from "@/Web/Components/TFLink/TFLink";
import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useParams } from "next/navigation";
import { HiOutlineSearch, HiOutlineDocumentText } from "react-icons/hi";
import { IoGridSharp } from "react-icons/io5";
import { useDirection } from "@/utils/useDirection";
import { useTranslations } from "next-intl";
import { HUB_URL_RELATIVE } from "@/config/domains";

const LetterByLetterText = () => {
  const t = useTranslations("web.pages.home.hero.animatedTexts");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const texts = [
    t("findHavruta"),
    t("chatInLiveRooms"),
    t("exploreTexts"),
    t("createSmartNotes"),
    t("getCommunitySupport"),
    t("prepareForHolidays"),
    t("findSagesComments"),
    t("askRabbisQuestions"),
  ];

  useEffect(() => {
    const currentText = texts[currentIndex];
    let timeout: NodeJS.Timeout;

    if (!isDeleting && displayText.length < currentText.length) {
      timeout = setTimeout(() => {
        setDisplayText(currentText.slice(0, displayText.length + 1));
      }, 50);
    } else if (!isDeleting && displayText.length === currentText.length) {
      timeout = setTimeout(() => {
        setIsDeleting(true);
      }, 2000);
    } else if (isDeleting && displayText.length > 0) {
      timeout = setTimeout(() => {
        setDisplayText(currentText.slice(0, displayText.length - 1));
      }, 20);
    } else if (isDeleting && displayText.length === 0) {
      setIsDeleting(false);
      setCurrentIndex((prev) => (prev + 1) % texts.length);
    }

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentIndex]);

  return (
    <div className="flex items-center justify-center h-20">
      <span className="text-4xl font-semibold block text-center">
        {displayText}
        <span className="animate-pulse">|</span>
      </span>
    </div>
  );
};

export default function SectionHero() {
  const direction = useDirection();
  const isRTL = direction === "rtl";
  const t = useTranslations("web.pages.home.hero");
  const router = useRouter();
  const params = useParams<{ locale: string }>();
  const locale = params?.locale || "ru";
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(
        `/${locale}/search?q=${encodeURIComponent(searchQuery.trim())}`,
      );
    }
  };

  const handleSearchButtonClick = () => {
    setIsSearchOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setTimeout(() => {
        if (
          !searchQuery.trim() &&
          document.activeElement !== searchInputRef.current
        ) {
          setIsSearchOpen(false);
        }
      }, 200);
    }
  };

  return (
    <section className="p-4">
      <div className="max-w-6xl mx-auto text-center space-y-6">
        <h1 className="text-6xl md:text-7xl font-light text-slate-900 leading-tight">
          {t("title")}{" "}
          <span
            className="font-semibold"
            style={{ textShadow: "0px 0px 5px rgba(0, 0, 0, 0.2)" }}
          >
            Tiferet
          </span>
        </h1>

        <p className="text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
          {t("subtitle")}
        </p>

        <LetterByLetterText />

        <div className="flex gap-4 justify-center items-center pt-4 w-full max-w-4xl mx-auto min-h-[48px]">
          <TFLink
            href={HUB_URL_RELATIVE}
            noLang={true}
            className={`bg-black text-white rounded-full font-medium hover:bg-black/90 transition-all duration-500 ease-in-out flex items-center justify-center relative overflow-hidden h-12 ${
              isSearchOpen ? "w-12 min-w-[48px] px-0" : "px-8 min-w-0"
            }`}
          >
            <IoGridSharp
              className={`transition-all duration-500 shrink-0 ${
                isSearchOpen
                  ? "w-5 h-5 opacity-100 relative"
                  : "w-0 h-0 opacity-0 absolute"
              }`}
            />
            <span
              className={`transition-all duration-500 whitespace-nowrap ${
                isSearchOpen
                  ? "max-w-0 opacity-0 overflow-hidden"
                  : "max-w-full opacity-100"
              }`}
            >
              {t("goToHub")}
            </span>
          </TFLink>

          <TFLink
            href="/news"
            className={`bg-black/5 text-black rounded-full font-medium hover:bg-black/10 transition-all duration-500 ease-in-out flex items-center justify-center relative overflow-hidden h-12 ${
              isSearchOpen ? "w-12 min-w-[48px] px-0" : "px-8 min-w-0"
            }`}
          >
            <HiOutlineDocumentText
              className={`transition-all duration-500 shrink-0 ${
                isSearchOpen
                  ? "w-5 h-5 opacity-100 relative"
                  : "w-0 h-0 opacity-0 absolute"
              }`}
            />
            <span
              className={`transition-all duration-500 whitespace-nowrap ${
                isSearchOpen
                  ? "max-w-0 opacity-0 overflow-hidden"
                  : "max-w-full opacity-100"
              }`}
            >
              {t("learnMore")}
            </span>
          </TFLink>

          <div
            className={`transition-all duration-500 ease-in-out ${
              isSearchOpen ? "flex-1 min-w-0 max-w-2xl" : "flex-none"
            }`}
          >
            {!isSearchOpen ? (
              <button
                onClick={handleSearchButtonClick}
                className="px-8 py-4 bg-black/5 text-black rounded-full font-medium hover:bg-black/10 transition-colors flex items-center gap-2 whitespace-nowrap h-12"
              >
                <HiOutlineSearch className="w-5 h-5 shrink-0" />
                {t("search")}
              </button>
            ) : (
              <form onSubmit={handleSearch} className="w-full">
                <div className="relative">
                  <div className="absolute inset-y-0 start-0 ps-4 flex items-center pointer-events-none">
                    <HiOutlineSearch className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder={t("searchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onBlur={handleSearchBlur}
                    dir={direction}
                    className="block w-full ps-12 pe-4 py-4 text-base border-2 border-slate-200 rounded-full bg-white focus:ring-4 focus:ring-purple-200/50 focus:border-purple-400 transition-all duration-300 hover:border-slate-300 h-12"
                  />
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
